import React, { useEffect, useState } from "react";
import UserScreenWindow from "../Components/UserScreenWindow";
import {
  queryAllDocsByFieldSnap,
  getDirectChatsSnap,
} from "../../../utils/firebase/Firebase.utils";
import GroupChatCard from "./GroupChatCard";
import Modal from "../../Dashboard/Components/Modal";
import CreateGroupChatForm from "./CreateGroupChatForm";
import { useSelector } from "react-redux";
import {
  FiPlus,
  FiSearch,
  FiUsers,
  FiMessageSquare,
  FiChevronRight,
  FiTag,
  FiShoppingBag,
} from "react-icons/fi";
import { Link } from "react-router-dom";

export default function GroupChatHome() {
  const currentUser = useSelector((state) => state.userObject);
  const [chats, setChats] = useState({ group: [], direct: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [createGroup, setCreateGroup] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // all, direct, group

  useEffect(() => {
    const fetchChats = async () => {
      await queryAllDocsByFieldSnap(
        "groupChats",
        "members",
        "array-contains",
        currentUser.uid,
        (data) => {
          data.sort((a, b) => {
            const timeA =
              a.lastMessageTime?.toDate() ||
              a.createdAt?.toDate() ||
              new Date(0);
            const timeB =
              b.lastMessageTime?.toDate() ||
              b.createdAt?.toDate() ||
              new Date(0);
            return timeB - timeA;
          });
          setChats((prev) => ({ ...prev, group: data }));
          setIsLoading(false);
        }
      );
    };

    const fetchDirectChats = async () => {
      await queryAllDocsByFieldSnap(
        "directChats",
        "members",
        "array-contains",
        currentUser.uid,
        (data) => {
          setChats((prev) => ({ ...prev, direct: data }));
        }
      );
    };

    const fetchAllChats = async () => {
      await Promise.all([fetchChats(), fetchDirectChats()]);
    };

    fetchAllChats();
  }, []);

  const getContextTitle = (chat) => {
    if (chat.productContext) {
      // Check all possible title fields
      return (
        chat.productContext.productTitle ||
        chat.productContext.propertyTitle ||
        chat.productContext.title ||
        chat.productContext.unitNumber ||
        "Direct Message"
      );
    }
    return "Direct Message";
  };

  const getContextPrice = (chat) => {
    if (chat.productContext) {
      return chat.productContext.price || chat.productContext.monthlyRent;
    }
    return null;
  };

  const getContextImage = (chat) => {
    if (chat.productContext) {
      return (
        chat.productContext.productImage ||
        chat.productContext.propertyImage ||
        chat.productContext.parkingImage ||
        chat.productContext.images?.[0]
      );
    }
    return null;
  };

  const filteredChats = {
    group: chats.group.filter((chat) =>
      chat.name.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    direct: chats.direct.filter(
      (chat) =>
        chat.members.some((member) =>
          member.toLowerCase().includes(searchQuery.toLowerCase())
        ) ||
        getContextTitle(chat)?.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  };

  const allChats = [...filteredChats.direct, ...filteredChats.group].sort(
    (a, b) => {
      const timeA =
        a.lastMessageTime?.toDate() || a.createdAt?.toDate() || new Date(0);
      const timeB =
        b.lastMessageTime?.toDate() || b.createdAt?.toDate() || new Date(0);
      return timeB - timeA;
    }
  );

  const displayChats =
    activeTab === "all"
      ? allChats
      : activeTab === "direct"
      ? filteredChats.direct
      : filteredChats.group;

  const createGroupChat = () => setCreateGroup(!createGroup);

  if (isLoading) {
    return (
      <UserScreenWindow label="Chats">
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#363062]"></div>
        </div>
      </UserScreenWindow>
    );
  }

  return (
    <div className="relative h-full bg-white">
      <UserScreenWindow label="Chats">
        <div className="flex flex-col h-full">
          {/* Header with Search and Tabs */}
          <div className="sticky top-0 z-10 bg-white border-b border-gray-100">
            <div className="px-4 py-3">
              <div className="flex justify-between items-center mb-3">
                <div className="relative flex-1 mr-3">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search chats"
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#363062]/20 text-[15px] placeholder-gray-400"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button
                  className="px-6 py-2.5 bg-[#8FCD00] text-white rounded-xl text-[15px] font-medium hover:bg-[#8FCD00]/90 transition-all duration-200 flex items-center whitespace-nowrap"
                  onClick={createGroupChat}
                >
                  <FiPlus className="mr-2" />
                  Create New Group
                </button>
              </div>

              {/* Tabs */}
              <div className="flex space-x-2 text-sm">
                <button
                  onClick={() => setActiveTab("all")}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                    activeTab === "all"
                      ? "bg-[#363062] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <FiMessageSquare />
                  All
                </button>
                <button
                  onClick={() => setActiveTab("direct")}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                    activeTab === "direct"
                      ? "bg-[#363062] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <FiTag />
                  Direct
                </button>
                <button
                  onClick={() => setActiveTab("group")}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                    activeTab === "group"
                      ? "bg-[#363062] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <FiUsers />
                  Groups
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto">
            {displayChats.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full px-4">
                <div className="w-16 h-16 rounded-full bg-[#363062]/10 flex items-center justify-center mb-4">
                  <FiMessageSquare className="text-2xl text-[#363062]" />
                </div>
                <p className="text-[17px] font-medium text-gray-900 mb-2">
                  No chats yet
                </p>
                <p className="text-[15px] text-center max-w-sm text-gray-500">
                  Start a conversation or create a group to begin chatting
                </p>
              </div>
            ) : (
              <div className="px-4 py-2 space-y-1">
                {displayChats.map((chat, index) => (
                  <Link key={index} to={`/chats/${chat.id}`} className="block">
                    <div className="group bg-white rounded-xl p-3 hover:bg-gray-50 transition-colors duration-200 cursor-pointer flex items-center justify-between border border-gray-100">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="w-10 h-10 rounded-full bg-[#363062]/10 flex items-center justify-center overflow-hidden">
                          {chat.type === "direct" ? (
                            chat.productContext ? (
                              getContextImage(chat) ? (
                                <img
                                  src={getContextImage(chat)}
                                  alt={getContextTitle(chat)}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <FiShoppingBag className="text-[#363062]" />
                              )
                            ) : (
                              <FiMessageSquare className="text-[#363062]" />
                            )
                          ) : (
                            <FiUsers className="text-[#363062]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-[15px] text-gray-900 truncate">
                            {chat.type === "direct" ? (
                              chat.productContext ? (
                                <span className="flex items-center gap-2">
                                  <FiShoppingBag className="text-[#8FCD00] w-4 h-4" />
                                  {getContextTitle(chat)}
                                </span>
                              ) : (
                                "Direct Message"
                              )
                            ) : (
                              chat.name
                            )}
                          </h3>
                          <div className="flex items-center gap-2">
                            {getContextPrice(chat) && (
                              <span className="text-[#8FCD00] text-sm font-medium">
                                ₱{getContextPrice(chat)}
                              </span>
                            )}
                            <p className="text-sm text-gray-500 truncate">
                              {chat.lastMessage || "No messages yet"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <FiChevronRight className="text-gray-400 group-hover:text-gray-600 transition-colors duration-200" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* FAB Button */}
          <button
            className="fixed bottom-6 right-6 w-14 h-14 bg-[#8FCD00] rounded-full flex items-center justify-center text-white shadow-lg hover:bg-[#8FCD00]/90 transition-all duration-200 hover:scale-105"
            onClick={createGroupChat}
          >
            <FiPlus className="text-2xl" />
          </button>

          {/* Create Group Modal */}
          {createGroup && (
            <Modal>
              <CreateGroupChatForm createGroupChat={createGroupChat} />
            </Modal>
          )}
        </div>
      </UserScreenWindow>
    </div>
  );
}
